<script setup lang="ts">
  /**
   * Upload field — 03-components/15a-form-controls · OSCloud DS 1.1.105
   *
   * The form-field foundation with a file slot: a trigger at the leading edge, the constraint
   * at the trailing edge, and the register of picked files underneath. The block owns three
   * things and nothing else — the trigger row, the native input's visually-hidden-but-focusable
   * presentation, and the gap above the register.
   *
   * The register IS a `BfRecordRowGroup` of `BfRecordRow` (filename as label, size as sub, one
   * destructive icon action per row), passed in the default slot. No thumbnails and no progress
   * bars: a file that has not been sent yet is a row, not a task.
   *
   * The visible label is optional — a closing picker in a group that has already named itself
   * omits it, and then the input carries its own `aria-label` and the constraint message is
   * required so it has something to describe it.
   */
  import { ref } from 'vue'

  withDefaults(
    defineProps<{
      /** Wires the input to its trigger and its message. Required — it is the field's identity. */
      id: string
      /** The visible label. Omit it inside a group that already names the field. */
      label?: string
      /** The accessible name when `label` is omitted. */
      ariaLabel?: string
      /** What may be picked, as the native `accept` list. */
      accept?: string
      /** Lets one pick take several files. */
      multiple?: boolean
      /** The standing constraint: allowed types, a maximum, a remaining count. */
      message?: string
      /** Why the last pick was rejected. Replaces `message` and is announced. */
      error?: string
      disabled?: boolean
      /** The trigger's words. */
      triggerLabel?: string
    }>(),
    {
      label: '',
      ariaLabel: undefined,
      accept: undefined,
      multiple: false,
      message: '',
      error: '',
      disabled: false,
      triggerLabel: 'Choose files',
    },
  )

  const emit = defineEmits<{ pick: [files: FileList] }>()

  const inputRef = ref<HTMLInputElement>()

  /**
   * The input is cleared after every pick so that choosing the same file twice in a row still
   * fires `change` — the browser suppresses it when the value has not moved.
   */
  const onChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    if (input.files?.length) {
      emit('pick', input.files)
    }
    input.value = ''
  }
</script>

<template>
  <div class="form-field upload-field">
    <label v-if="label" class="form-field__label" :for="id">{{ label }}</label>

    <div class="form-field__control upload-field__row">
      <!-- Visually hidden, NOT display:none — the input keeps its focus stop, its label
           association and its validation, and the trigger is styled chrome in front of it.
           Focusing the input rings the trigger, so keyboard use is unchanged. -->
      <input
        :id="id"
        ref="inputRef"
        class="upload-field__input"
        type="file"
        :accept="accept"
        :multiple="multiple"
        :disabled="disabled"
        :aria-label="label ? undefined : (ariaLabel ?? triggerLabel)"
        :aria-describedby="message || error ? `${id}-message` : undefined"
        :aria-invalid="error ? 'true' : undefined"
        @change="onChange"
      />
      <!-- The input is the control; the button is its face. The button is not itself focusable
           chrome over a dead input — focus stays on the input, which is what paints the ring. -->
      <button type="button" class="upload-field__trigger" tabindex="-1" :disabled="disabled" @click="inputRef?.click()">
        {{ triggerLabel }}
      </button>
      <div
        v-if="message || error"
        :id="`${id}-message`"
        class="form-field__message"
        :class="{ 'form-field__message--error': error }"
        :role="error ? 'alert' : undefined"
      >
        {{ error || message }}
      </div>
    </div>

    <div v-if="$slots.default" class="upload-field__list">
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
  /* The field is its own query container, so the row wraps on the FIELD's width — a picker in a
     320px rail column and one in a 720px measure behave the same way regardless of what
     surrounds them. */
  .upload-field {
    container-type: inline-size;
  }

  .form-field__label {
    display: block;
    margin-bottom: 6px;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-xs_5);
    font-weight: var(--bf-fw-semibold);
    color: var(--bf-deep-blue);
  }

  /* One line: trigger at the leading edge, constraint at the trailing edge. */
  .upload-field__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--bf-sp-2);
  }

  .form-field__message {
    margin-left: auto;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs);
    line-height: var(--bf-lh-normal);
    text-align: right;
    color: var(--bf-fg-2);
  }

  .form-field__message--error {
    color: var(--bf-form-invalid-text);
  }

  .upload-field__input {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    white-space: nowrap;
    border: 0;
    clip-path: inset(50%);
  }

  /* The outline button's face, restated here rather than composed from BfButton: the focus and
     invalid rules below select the trigger as the input's adjacent sibling, which only holds
     while the trigger is this block's own element. */
  .upload-field__trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    padding: 0 12px;
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-md);
    background: var(--bf-white);
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-xs);
    font-weight: var(--bf-fw-semibold);
    cursor: pointer;
    color: var(--bf-deep-blue);
    transition:
      background var(--bf-dur-fast) var(--bf-ease),
      border-color var(--bf-dur-fast) var(--bf-ease);
  }

  .upload-field__trigger:hover {
    border-color: var(--bf-dusky-2);
    background: var(--bf-blue-light);
  }

  /* Focus lives on the input; the ring is painted on the trigger beside it. */
  .upload-field__input:focus-visible + .upload-field__trigger {
    outline: 2px solid var(--bf-orange-dark);
    outline-offset: 2px;
  }

  /* A rejected file is the field's error, so the trigger reads it off the input's aria-invalid
     and the message beside it is the reason. */
  .upload-field__input[aria-invalid='true'] + .upload-field__trigger {
    border-color: var(--bf-form-invalid-hairline);
    color: var(--bf-form-invalid-text);
  }

  .upload-field__input:disabled + .upload-field__trigger {
    opacity: 0.5;
    pointer-events: none;
  }

  /* The register sits under the row at the field's own rhythm. */
  .upload-field__list {
    margin-top: var(--bf-sp-2);
  }

  @container (max-width: 420px) {
    .form-field__message {
      margin-left: 0;
      text-align: left;
    }
  }
</style>
